import { z } from "zod";
import { DateTime } from "luxon";
import { Length } from "@shared/common/utils/length";
import { Action, Changeability, Orientation } from "./models/action";
import {
  ACTION_TYPES,
  ActionType,
  MOVEMENT_ACTION_TYPES,
  SINGLE_TRACK_ACTION_TYPES,
} from "./models/actionType";
import type { components } from "@opstel/gen/api/v2"; // generated by openapi-typescript

const actionTypeSchema = z.enum(ACTION_TYPES) satisfies z.ZodType<ActionType>;
const changeabilitySchema = z.enum(["Fixed", "Pinned", "Open"]) satisfies z.ZodType<Changeability>;
const orientationSchema = z.enum([
  "FirstUnitOnTargetSide",
  "FirstUnitOnSourceSide",
]) satisfies z.ZodType<Orientation>;
const lengthSchema = z
  .object({ millimeters: z.number() })
  .transform((l) => new Length(l.millimeters));

const baseActionSchema = z.object({
  id: z.uuid(),
  type: actionTypeSchema,
  shuntingUnitId: z.uuid(),
  name: z.string(),
  startTime: z.iso
    .datetime()
    .transform((dt) => DateTime.fromISO(dt))
    .refine((d) => d.isValid, "Invalid timestamp"),
  endTime: z.iso
    .datetime()
    .transform((dt) => DateTime.fromISO(dt))
    .refine((d) => d.isValid, "Invalid timestamp"),
  changeability: changeabilitySchema,
  orientation: orientationSchema,
}) satisfies z.ZodType<unknown, components["schemas"]["BaseAction"]>;

const moveActionSchema = baseActionSchema.extend({
  objectType: z.literal("MovementAction"),
  type: z.enum(MOVEMENT_ACTION_TYPES),
  tracks: z.array(z.string()),
  isNetworkMovement: z.boolean(),
  distanceToSourceStart: lengthSchema,
  distanceToSourceEnd: lengthSchema,
  stablingInfraStart: z.string(),
  stablingInfraEnd: z.string(),
  movementCode: z.string().optional(),
}) satisfies z.ZodType<unknown, components["schemas"]["MovementAction"]>;

const singleTrackActionSchema = baseActionSchema.extend({
  objectType: z.literal("SingleTrackAction"),
  type: z.enum(SINGLE_TRACK_ACTION_TYPES),
  distanceToSource: lengthSchema,
  stablingInfra: z.string(),
}) satisfies z.ZodType<unknown, components["schemas"]["SingleTrackAction"]>;

export const actionSchema = z.discriminatedUnion("type", [
  moveActionSchema,
  singleTrackActionSchema,
]) satisfies z.ZodType<Action, components["schemas"]["BaseAction"]>;
